import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';

import { TrilogyComponentsModule } from '@app/base/trilogy.module';
import { ConfiguratorType } from '@app/baseBtl/catalog/products/interface/configurable';
import { ApiFunding } from '@app/baseBtl/catalog/products/interface/context';
import { FundingConfiguratorComponent } from '@app/modules/product-details/funding-configurator/funding-configurator.component';

import { ModalRef, ModalService } from '@trilogy';
import { Subject, takeUntil } from 'rxjs';

import { BytelTagService } from '../../../../base/services/bytel-tag.service';
import { ViewService } from '../../../../base/services/view.service';
import { Configurator } from '../../interface/configurable';
import { ModalCmsComponent } from '../modals/modal-cms/modal-cms.component';

@Component({
    selector: 'btl-configurator',
    templateUrl: './configurator.component.html',
    styleUrls: ['./configurator.component.scss'],
    standalone: true,
    imports: [FundingConfiguratorComponent, CommonModule, TrilogyComponentsModule],
})
export class ConfiguratorComponent implements OnInit, OnDestroy {
    public configurators: Configurator[] = [];
    public enabledConfigurators: Configurator[];
    public configuratorStatus: { [key: string]: number } = {};
    private readonly STATUS = {
        STARTED: 0,
        INIT: 1,
        CLICKED: 2,
    };

    public configuratorValues = {
        Oui: true,
        Non: false,
    };

    public fundings: ApiFunding[];

    private _scavenger = new Subject();

    public configLabels: string[] = ['Oui', 'Non'];
    public isWithoutTax: boolean = false;

    constructor(
        private viewService: ViewService,
        private tagService: BytelTagService,
        private modalService: ModalService,
    ) {}

    public ngOnInit(): void {
        this.viewService.configuratorsSubject
            .pipe(takeUntil(this._scavenger))
            .subscribe((configurators: Configurator[]) => {
                configurators = configurators.map((configurator: Configurator) => {
                    this.configuratorStatus[configurator.id] = this.STATUS.STARTED;
                    if (configurator && !configurator.enabled) {
                        configurator.options = !!configurator.value ? ['Oui'] : ['Non'];
                        configurator.selected = !!configurator.value ? 'Oui' : 'Non';
                        return configurator;
                    }
                    configurator.options = ['Oui', 'Non'];
                    configurator.selected = 'Oui';
                    return configurator;
                });
                this.configurators = configurators;
                this.enabledConfigurators = this.showEnabledConfigurator(configurators);
            });

        this.isWithoutTax = this.viewService.canApplyWithoutTaxSignal();

        this.viewService.fundingsSubject.subscribe((fundings: ApiFunding[]) => {
            this.fundings = fundings;
        });
    }

    public ngOnDestroy(): void {
        // Used for scavenger observable unsubscriptions
        this._scavenger.next(null);
        this._scavenger.complete();
    }

    public selectConfigurator(configurator: Configurator, value: string): void {
        this.configuratorStatus[configurator.id] = this.configuratorStatus[configurator.id]
            ? this.STATUS.CLICKED
            : this.STATUS.INIT;

        const isEdpCase = configurator.id === ConfiguratorType.EDP;
        configurator.value = isEdpCase ? value : this.configuratorValues[value];

        this.viewService.addConfigurator(configurator, isEdpCase);

        const clic = configurator.value ? 'Oui' : 'Non';
        let config = '';
        switch (configurator.id) {
            case 'porta':
                config = 'Infos_telephone_numero';
                break;
            case 'EDP':
                config = 'Infos_telephone_etalement_paiement';
                break;
        }
        if (config && this.configuratorStatus[configurator.id] === this.STATUS.CLICKED) {
            this.tagService.send({
                nomClic: clic,
                categorieClic: config,
                actionClic: 'Clic_selection',
            });
        }
    }

    public showEnabledConfigurator(configurators: Configurator[]): Configurator[] {
        const configs: Configurator[] = configurators.filter((conf) => conf.show);
        return configs;
    }

    public showModal(blockId): void {
        const modalRef: ModalRef<ModalCmsComponent> = this.modalService.open<ModalCmsComponent>(ModalCmsComponent, {
            modalSize: 'large',
        });
        const modalComponent: ModalCmsComponent = modalRef.componentInstance;
        modalComponent.initCmsBlock(`${blockId}${this.isWithoutTax ? '-pro' : ''}`);
    }
}
